import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type { Market } from '../types';

const CACHE_KEY = 'markets_cache';
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

interface CachedData {
  data: Market[];
  timestamp: number;
}

function getCachedMarkets(): Market[] | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, timestamp }: CachedData = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL) {
        return data;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

function setCachedMarkets(markets: Market[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data: markets,
      timestamp: Date.now()
    }));
  } catch {
    // Ignore cache errors (quota exceeded, etc.)
  }
}

interface UseMarketsResult {
  markets: Market[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useMarkets(): UseMarketsResult {
  // Initialize from cache if available
  const [markets, setMarkets] = useState<Market[]>(() => getCachedMarkets() || []);
  const [loading, setLoading] = useState(() => getCachedMarkets() === null);
  const [error, setError] = useState<string | null>(null);

  const fetchMarkets = useCallback(async (forceRefresh = false) => {
    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = getCachedMarkets();
      if (cached) {
        setMarkets(cached);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const data = await api.getMarkets();
      const marketList = data.markets || [];
      setMarkets(marketList);
      setCachedMarkets(marketList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load markets');
    } finally {
      setLoading(false);
    }
  }, []);

  // Force refresh function (bypasses cache)
  const refresh = useCallback(async () => {
    await fetchMarkets(true);
  }, [fetchMarkets]);

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  return { markets, loading, error, refresh };
}
